/**
 * Session helpers for CWE-613 (invalidate session after password change).
 * Shared by logout action, ChangePassword, and API auth-failure handling.
 *
 * Note: Cross-browser session kill requires the API to revoke old tokens
 * after password change. This module clears local session and reacts when
 * the API rejects an invalid/expired session.
 */

export const AUTH_STORAGE_KEYS = [
  "user",
  "token",
  "roleId",
  "sectionId",
  "userId",
  "guid",
];

export const FORCE_LOGOUT_KEY = "forceLogout";

export const clearAuthStorage = () => {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

/**
 * Signal other same-browser tabs to log out (via the `storage` event).
 */
export const signalForceLogout = () => {
  localStorage.setItem(FORCE_LOGOUT_KEY, Date.now().toString());
};

/**
 * Clear auth storage and optionally hard-redirect to login.
 * Used when API rejects an expired/invalid session.
 */
export const clearSessionAndRedirect = ({ redirect = true } = {}) => {
  clearAuthStorage();
  localStorage.removeItem(FORCE_LOGOUT_KEY);

  if (!redirect) return;

  const path = (window.location.pathname || "").toLowerCase();
  if (path === "/" || path.endsWith("/login")) return;

  window.location.replace("/");
};

/**
 * Detect API responses that mean the session/token is no longer valid.
 */
export const isUnauthorizedResponse = (response, data) => {
  if (response && (response.status === 401 || response.status === 403)) {
    return true;
  }

  if (!data || typeof data !== "object") return false;

  const rData = data.rData || data;
  const code = rData.rCode ?? data.rCode ?? data.rStatus;
  const message = String(
    rData.rMessage || data.rMessage || data.message || ""
  ).toLowerCase();

  if (code === 401 || code === 403) return true;

  return (
    message.includes("unauthorized") ||
    message.includes("unauthorised") ||
    message.includes("invalid token") ||
    message.includes("token expired") ||
    message.includes("session expired") ||
    message.includes("session invalid") ||
    message.includes("please login") ||
    message.includes("not authenticated")
  );
};
